import MailOutlineIcon from '@mui/icons-material/MailOutline'
import Chip from 'core-system/Chip'
import CreditCardIcon from 'core-system/Icons/Accounts/CreditCard'
import LocationIcon from 'core-system/Icons/Misc/Location'
import Text from 'core-system/Text'
import palette from 'core-system/Themes/palette'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import { employeeActions } from 'redux/employee/employeeSlice'
import {
  FleetCardReplacementReason,
  FleetCardSummary,
  FleetCardType,
} from 'redux/employee/employeeTypes'
import styled from 'styled-components'
import { getFleetCardPillLabel, getFleetCardPillVariant } from '../utils'
import AddFleetCardModal from './AddFleetCardModal'
import ManageFleetCardModal, {
  FleetCardManageAction,
} from './ManageFleetCardModal'

const Section = styled.div`
  margin-top: 1rem;
`

const CardRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
`

const DetailCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  min-width: 0;
  padding: 0.75rem;
  border: 1px solid ${(props) => props.theme.palette.grey.grey300};
  border-radius: 0.5rem;
  background-color: ${(props) => props.theme.palette.white};
`

const IconWell = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 0.3125rem;
  background-color: ${(props) => props.theme.palette.secondary.purple200};
  border: 1px solid ${(props) => props.theme.palette.secondary.purple300};
`

const CardBody = styled.div`
  min-width: 0;
  flex: 1;
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

const CardValue = styled(Text)`
  margin-top: 0.125rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0.375rem;
`

const ClickableChip = styled(Chip)`
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`

const AddCardButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${(props) => props.theme.palette.primary.primaryDark};
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.25;
  white-space: nowrap;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    cursor: default;
    color: ${(props) => props.theme.palette.text.disabled};
  }
`

interface CommuterBasicInfoProps {
  employeeId: string
  email?: string
  worksiteAlias?: string
  address?: string | null
  fleetCards?: FleetCardSummary[]
}

const CommuterBasicInfo = (props: CommuterBasicInfoProps) => {
  const {
    employeeId,
    email,
    worksiteAlias,
    address = null,
    fleetCards = [],
  } = props
  const dispatch = useDispatch()
  const manageFleetCardStatus = useSelector(
    (state: AppState) => state.employee.manageFleetCardStatus
  )
  const hasFleetCard = fleetCards.length > 0
  const [selectedCard, setSelectedCard] = useState<FleetCardSummary | null>(
    null
  )
  const [isAddCardOpen, setIsAddCardOpen] = useState(false)
  useEffect(() => {
    if (!selectedCard) {
      return
    }
    const updatedCard = fleetCards.find(
      (card) => card.internalId === selectedCard.internalId
    )
    if (updatedCard && updatedCard.status !== selectedCard.status) {
      setSelectedCard(updatedCard)
    }
  }, [fleetCards, selectedCard])

  useEffect(() => {
    if (manageFleetCardStatus === 'success') {
      setSelectedCard(null)
      setIsAddCardOpen(false)
      dispatch(employeeActions.toggleManageFleetCardStatusOff())
    }
  }, [manageFleetCardStatus, dispatch])

  const detailDateRange = () => {
    const currentMoment = moment()
    return {
      detailStartDate: currentMoment.startOf('month').format(),
      detailEndDate: currentMoment.endOf('month').format(),
    }
  }

  const handleFleetCardAction = (
    action: FleetCardManageAction,
    card: FleetCardSummary,
    options?: {
      replacementReason?: FleetCardReplacementReason
      shippingAddress?: string
    }
  ) => {
    if (action === 'activate') {
      dispatch(
        employeeActions.activateFleetCard({
          employeeId,
          cardInternalId: card.internalId,
        })
      )
      return
    }
    if (action === 'deactivate') {
      dispatch(
        employeeActions.deactivateFleetCard({
          employeeId,
          cardInternalId: card.internalId,
        })
      )
      return
    }
    if (action === 'replace' && options?.replacementReason) {
      dispatch(
        employeeActions.replaceFleetCard({
          employeeId,
          cardInternalId: card.internalId,
          replacementReason: options.replacementReason,
          overrideShippingAddress: options.shippingAddress,
          ...detailDateRange(),
        })
      )
    }
  }

  const handleAddCard = (cardType: FleetCardType, shippingAddress?: string) => {
    dispatch(
      employeeActions.createFleetCard({
        employeeId,
        cardType,
        overrideShippingAddress: shippingAddress,
        ...detailDateRange(),
      })
    )
  }

  return (
    <Section>
      <CardRow>
        <DetailCard>
          <IconWell>
            <MailOutlineIcon
              sx={{ fontSize: 16, color: palette.text.secondary }}
            />
          </IconWell>
          <CardBody>
            <Text variant='caption' textColor={palette.text.secondary}>
              Email
            </Text>
            <CardValue variant='action4'>{email || 'None'}</CardValue>
          </CardBody>
        </DetailCard>
        <DetailCard>
          <IconWell>
            <LocationIcon
              width={16}
              height={16}
              color={palette.text.secondary}
            />
          </IconWell>
          <CardBody>
            <Text variant='caption' textColor={palette.text.secondary}>
              Worksite
            </Text>
            <CardValue variant='action4'>{worksiteAlias || 'None'}</CardValue>
          </CardBody>
        </DetailCard>
        <DetailCard>
          <IconWell>
            <CreditCardIcon
              width={16}
              height={16}
              color={palette.text.secondary}
            />
          </IconWell>
          <CardBody>
            <CardHeader>
              <Text variant='caption' textColor={palette.text.secondary}>
                Fleet Card
              </Text>
              <AddCardButton
                type='button'
                disabled={manageFleetCardStatus === 'loading'}
                onClick={() => setIsAddCardOpen(true)}
              >
                + Add
              </AddCardButton>
            </CardHeader>
            {hasFleetCard ? (
              <PillRow>
                {fleetCards.map((card) => (
                  <ClickableChip
                    key={card.internalId}
                    variant={getFleetCardPillVariant(card)}
                    role='button'
                    tabIndex={0}
                    aria-label={`Manage ${getFleetCardPillLabel(card)} card`}
                    onClick={() => setSelectedCard(card)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        setSelectedCard(card)
                      }
                    }}
                  >
                    {getFleetCardPillLabel(card)}
                  </ClickableChip>
                ))}
              </PillRow>
            ) : (
              <CardValue variant='action4'>None</CardValue>
            )}
          </CardBody>
        </DetailCard>
      </CardRow>
      <ManageFleetCardModal
        open={Boolean(selectedCard)}
        card={selectedCard}
        shippingAddress={address}
        isSubmitting={manageFleetCardStatus === 'loading'}
        onClose={() => {
          setSelectedCard(null)
          if (manageFleetCardStatus === 'failed') {
            dispatch(employeeActions.toggleManageFleetCardStatusOff())
          }
        }}
        onAction={handleFleetCardAction}
      />
      <AddFleetCardModal
        open={isAddCardOpen}
        existingCards={fleetCards}
        shippingAddress={address}
        isSubmitting={manageFleetCardStatus === 'loading'}
        onClose={() => {
          setIsAddCardOpen(false)
          if (manageFleetCardStatus === 'failed') {
            dispatch(employeeActions.toggleManageFleetCardStatusOff())
          }
        }}
        onAdd={handleAddCard}
      />
    </Section>
  )
}

export default CommuterBasicInfo
